import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import DOMPurify from 'dompurify';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './RichTextEditor.css';

const RichTextEditor = ({ value, onChange, placeholder = 'Write a detailed product description...', className = '' }) => {
  const [editorState, setEditorState] = useState(() => {
    // Initialize editor with existing HTML content if available
    if (value) {
      try {
        const blocksFromHTML = convertFromHTML(value);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        return EditorState.createWithContent(contentState);
      } catch (error) {
        console.error('Error parsing HTML content:', error);
        return EditorState.createEmpty();
      }
    }
    return EditorState.createEmpty();
  });

  const handleEditorChange = (state) => {
    setEditorState(state);
    
    // Convert editor state to HTML
    const rawContent = convertToRaw(state.getCurrentContent());
    const html = draftToHtml(rawContent);
    
    // Sanitize HTML to prevent XSS
    const sanitizedHtml = DOMPurify.sanitize(html);
    
    // Call parent's onChange with sanitized HTML
    onChange(sanitizedHtml);
  };

  // Toolbar configuration
  const toolbar = {
    options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'remove', 'history'],
    inline: {
      options: ['bold', 'italic', 'underline', 'strikethrough'],
    },
    blockType: {
      inDropdown: true,
      options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
    },
    fontSize: {
      options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36],
    },
    list: {
      options: ['unordered', 'ordered', 'indent', 'outdent'],
    },
    textAlign: {
      options: ['left', 'center', 'right', 'justify'],
    },
    colorPicker: {
      colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
        'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)',
        'rgb(0,168,133)', 'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)',
        'rgb(40,50,78)', 'rgb(0,0,0)', 'rgb(247,218,100)', 'rgb(251,160,38)',
        'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)', 'rgb(239,239,239)',
        'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
        'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)'],
    },
    link: {
      options: ['link', 'unlink'],
    },
    remove: {},
    history: {
      options: ['undo', 'redo'],
    },
  };

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
        toolbar={toolbar}
        placeholder={placeholder}
        editorClassName="rich-text-editor-content"
        toolbarClassName="rich-text-editor-toolbar"
      />
    </div>
  );
};

export default RichTextEditor;
